import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaRegStar, FaStar } from "react-icons/fa";
import { FiHeart, FiInfo, FiShoppingBag } from "react-icons/fi";
import { T } from "../../constants/theme";
import { PRODUCT_TYPE_OPTIONS, PRODUCT_SIZE_OPTIONS } from "../../constants/product";
import { CartContext } from "../../contexts/CartContext";
import { AuthContext } from "../../contexts/AuthContext";
import { ToastContext } from "../../contexts/ToastContext";
import { WishlistContext } from "../../contexts/WishlistContext";
import { useApi } from "../../hooks/useApi";
import { requireLogin } from "../../utils/authHelpers";
import { getProjectTelemetry } from "../../services/api";
import AccordionRow from "../../components/AccordionRow";
import TelemetrySparkline from "../../components/TelemetrySparkline";

const TELEMETRY_POLL_MS = 30000;

export default function ProductDetail() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { data: product, loading } = useApi("/products/" + slug, [slug]);
  const { addItem } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const { isWishlisted, toggleWishlist } = useContext(WishlistContext);
  const [qty, setQty] = useState(1);
  const [selectedType, setSelectedType] = useState(PRODUCT_TYPE_OPTIONS[0]);
  const [selectedSize, setSelectedSize] = useState(PRODUCT_SIZE_OPTIONS[0]);
  const [openSection, setOpenSection] = useState(null);
  const [telemetry, setTelemetry] = useState(null);

  useEffect(() => {
    if (!product?.id) return undefined;
    let cancelled = false;

    const load = () => {
      getProjectTelemetry(product.id, { sinceHours: 24 }).then((data) => {
        if (!cancelled) setTelemetry(data);
      });
    };

    load();
    const interval = setInterval(load, TELEMETRY_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [product?.id]);

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: 80, color: T.muted }}>
        Loading…
      </div>
    );

  if (!product)
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        Product not found
      </div>
    );

  const hasDiscount =
    product.sale_price && product.sale_price < product.price;

  const price = parseFloat(product.sale_price || product.price);
  const fullPrice = parseFloat(product.price);

  return (
    <div
      style={{
        background: T.paper,
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          padding: "60px 24px 90px",
        }}
      >
        {/* BACK */}
        <button
          onClick={() => navigate("/shop")}
          style={{
            background: "transparent",
            border: "none",
            color: T.ink,
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: ".14em",
            textTransform: "uppercase",
            marginBottom: 28,
          }}
        >
          ← Back to Projects
        </button>

        {/* MAIN CARD */}
        <div
          style={{
            background: T.surface,
            border: "1px solid rgba(95,203,140,.12)",
            borderRadius: 28,
            boxShadow: "0 30px 80px rgba(0,0,0,.08)",
            overflow: "hidden",
            padding: 40,
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
              gap: 56,
            }}
          >
            {/* IMAGE */}
            <div
              style={{
                position: "relative",
                borderRadius: 16,
                overflow: "hidden",
                background: T.surface,
                border: `1px solid ${T.border}`,
                boxShadow: T.shadowSoft,
                aspectRatio: "3 / 4",
              }}
            >
              {product.images?.[0]?.url ? (
                <img
                  src={product.images[0].url}
                  alt={product.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 100,
                  }}
                >
                  🌾
                </div>
              )}
            </div>

            {/* CONTENT */}
            <div>
              {/* SALE BADGE */}
              {hasDiscount && (
                <span
                  style={{
                    display: "inline-block",
                    background: "#D14343",
                    color: "#fff",
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: ".1em",
                    textTransform: "uppercase",
                    padding: "5px 12px",
                    borderRadius: 3,
                    marginBottom: 16,
                  }}
                >
                  Sale
                </span>
              )}

              {/* TITLE */}
              <h1
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 34,
                  fontWeight: 800,
                  textTransform: "uppercase",
                  letterSpacing: ".02em",
                  margin: "0 0 14px",
                  color: T.ink,
                }}
              >
                {product.name}
              </h1>

              {/* PRICE */}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <span
                  style={{
                    fontSize: 26,
                    fontWeight: 800,
                    color: hasDiscount ? "#D14343" : T.ink,
                  }}
                >
                  LKR {price.toFixed(2)}
                </span>

                {hasDiscount && (
                  <span
                    style={{
                      fontSize: 17,
                      color: T.muted,
                      textDecoration: "line-through",
                    }}
                  >
                    LKR {fullPrice.toFixed(2)}
                  </span>
                )}
              </div>

              {/* RATING */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 20,
                }}
              >
                <div style={{ display: "flex", gap: 2 }}>
                  {Array.from({ length: 5 }).map((_, i) =>
                    i < Math.round(Number(product.rating_avg) || 0) ? (
                      <FaStar key={i} style={{ color: T.gold, fontSize: 14 }} />
                    ) : (
                      <FaRegStar key={i} style={{ color: T.gold, fontSize: 14 }} />
                    )
                  )}
                </div>

                <button
                  onClick={() => setOpenSection("reviews")}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: T.muted,
                    fontSize: 13,
                    textDecoration: "underline",
                  }}
                >
                  (
                  {Number(product.rating_count) || product.reviews?.length || 0}{" "}
                  Customer Reviews)
                </button>
              </div>

              {/* DESCRIPTION */}
              <p
                style={{
                  color: T.inkSoft,
                  fontSize: 15,
                  lineHeight: 1.8,
                  marginBottom: 26,
                  maxWidth: 480,
                }}
              >
                {product.short_desc}
              </p>

              {/* FUNDING PROGRESS */}
              {product.funding_goal > 0 && (
                <div style={{ marginBottom: 26, maxWidth: 480 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: ".05em",
                      textTransform: "uppercase",
                      color: T.ink,
                      marginBottom: 8,
                    }}
                  >
                    <span>
                      LKR {Number(product.funding_raised || 0).toLocaleString()} raised
                    </span>
                    <span style={{ color: T.muted }}>
                      of LKR {Number(product.funding_goal).toLocaleString()} goal
                    </span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 4,
                      background: T.border,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.min(
                          100,
                          (Number(product.funding_raised || 0) / Number(product.funding_goal)) * 100
                        )}%`,
                        background: T.gold,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 16,
                      marginTop: 10,
                      fontSize: 13,
                      color: T.muted,
                    }}
                  >
                    {product.roi_percent != null && (
                      <span>Target ROI: <strong style={{ color: T.ink }}>{product.roi_percent}%</strong></span>
                    )}
                    {product.duration_days != null && (
                      <span>Duration: <strong style={{ color: T.ink }}>{product.duration_days} days</strong></span>
                    )}
                    {product.location && (
                      <span>Location: <strong style={{ color: T.ink }}>{product.location}</strong></span>
                    )}
                  </div>
                </div>
              )}

              {/* COLOR
              <div style={{ marginBottom: 22 }}>
                <p
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: ".1em",
                    textTransform: "uppercase",
                    color: T.ink,
                    marginBottom: 10,
                  }}
                >
                  Type: {selectedType}
                </p>

                <div style={{ display: "flex", gap: 10 }}>
                  {PRODUCT_TYPE_OPTIONS.map((c) => (
                    <button
                      key={c}
                      onClick={() => setSelectedType(c)}
                      style={{
                        padding: "10px 18px",
                        borderRadius: 6,
                        border: `1px solid ${
                          selectedType === c ? T.ink : T.border
                        }`,
                        background: selectedType === c ? T.ink : T.surface,
                        color: selectedType === c ? T.paper : T.ink,
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: ".05em",
                        textTransform: "uppercase",
                        cursor: "pointer",
                      }}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* SIZE */}
              {/* <div style={{ marginBottom: 26 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 10,
                  }}
                >
                  <p
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: ".1em",
                      textTransform: "uppercase",
                      color: T.ink,
                      margin: 0,
                    }}
                  >
                    Size: {selectedSize}
                  </p>

                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: ".08em",
                      textTransform: "uppercase",
                      color: T.gold,
                      cursor: "pointer",
                    }}
                  >
                    <FiInfo /> Size Guide
                  </span>
                </div>

                <div style={{ display: "flex", gap: 10 }}>
                  {PRODUCT_SIZE_OPTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSelectedSize(s)}
                      style={{
                        width: 56,
                        height: 44,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 6,
                        border: `1px solid ${
                          selectedSize === s ? T.ink : T.border
                        }`,
                        background: selectedSize === s ? T.ink : T.surface,
                        color: selectedSize === s ? T.paper : T.ink,
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div> */} 

              {/* QUANTITY + CTA ROW */}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  marginBottom: 20,
                }}
              >
                {/* qty */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    border: `1px solid ${T.border}`,
                    borderRadius: 6,
                    height: 48,
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    style={{
                      width: 42,
                      height: "100%",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      fontSize: 16,
                    }}
                  >
                    −
                  </button>

                  <span
                    style={{
                      width: 42,
                      textAlign: "center",
                      fontWeight: 700,
                    }}
                  >
                    {qty}
                  </span>

                  <button
                    onClick={() => setQty(qty + 1)}
                    style={{
                      width: 42,
                      height: "100%",
                      border: "none",
                      background: "transparent",
                      cursor: "pointer",
                      fontSize: 16,
                    }}
                  >
                    +
                  </button>
                </div>

                {/* CTA BUTTON */}
                <button
                  onClick={() => {
                    if (!requireLogin(user, navigate, toast, "Please sign in to invest in this project.")) {
                      return;
                    }
                    addItem(product.id, qty);
                  }}
                  style={{
                    flex: 1,
                    height: 48,
                    padding: "0 28px",
                    border: "none",
                    borderRadius: 6,
                    background: T.ink,
                    color: T.paper,
                    fontWeight: 700,
                    letterSpacing: ".12em",
                    textTransform: "uppercase",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    whiteSpace: "nowrap",
                    fontSize: 13,
                  }}
                >
                  <FiShoppingBag /> Invest
                </button>

                {/* WISHLIST BUTTON */}
                <button
                  onClick={() => {
                    if (!requireLogin(user, navigate, toast, "Please sign in to save items to your wishlist.")) {
                      return;
                    }
                    toggleWishlist({
                      id: product.id,
                      name: product.name,
                      slug: product.slug,
                      price: product.price,
                      sale_price: product.sale_price,
                      stock: product.stock,
                      image: product.images?.[0]?.url,
                    });
                  }}
                  aria-label="Add to wishlist"
                  style={{
                    width: 48,
                    height: 48,
                    flex: "0 0 auto",
                    border: `1px solid ${T.border}`,
                    borderRadius: 6,
                    background: T.surface,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: isWishlisted(product.id) ? "#D14343" : T.ink,
                  }}
                >
                  <FiHeart fill={isWishlisted(product.id) ? "#D14343" : "none"} />
                </button>
              </div>

              {/* INFO */}
              <p style={{ fontSize: 13, color: T.muted, marginBottom: 30 }}>
                {product.stock > 10
                  ? "✓ In stock"
                  : product.stock > 0
                  ? `⚠ Only ${product.stock} left`
                  : "✗ Out of stock"}{" "}
                · Free delivery over LKR 40
              </p>

              {/* ACCORDION */}
              <div style={{ borderTop: "1px solid rgba(0,0,0,.06)" }}>
                <AccordionRow
                  title="Material & Care"
                  isOpen={openSection === "material"}
                  onToggle={() =>
                    setOpenSection((s) => (s === "material" ? null : "material"))
                  }
                >
                  {product.description || product.short_desc}
                </AccordionRow>

                <AccordionRow
                  title="Shipping & Returns"
                  isOpen={openSection === "shipping"}
                  onToggle={() =>
                    setOpenSection((s) => (s === "shipping" ? null : "shipping"))
                  }
                >
                  Free standard delivery on orders over LKR 40. Orders are
                  dispatched within 1–2 business days. Returns are accepted
                  within 30 days of delivery for unworn items with original
                  tags attached.
                </AccordionRow>

                <AccordionRow
                  title={`Reviews (${product.reviews?.length || 0})`}
                  isOpen={openSection === "reviews"}
                  onToggle={() =>
                    setOpenSection((s) => (s === "reviews" ? null : "reviews"))
                  }
                >
                  {product.reviews?.length === 0 && (
                    <p style={{ color: T.muted, margin: 0 }}>
                      No reviews yet.
                    </p>
                  )}

                  {product.reviews?.map((r, i) => (
                    <div key={i} style={{ marginBottom: 16 }}>
                      <strong style={{ color: T.ink }}>{r.title}</strong>
                      <p style={{ margin: "4px 0 0" }}>{r.body}</p>
                    </div>
                  ))}
                </AccordionRow>
              </div>
            </div>
          </div>
        </div>

        {/* LIVE TELEMETRY */}
        {telemetry?.devices > 0 && (
          <div
            style={{
              marginTop: 40,
              background: T.surface,
              border: "1px solid rgba(95,203,140,.12)",
              borderRadius: 28,
              boxShadow: "0 30px 80px rgba(0,0,0,.08)",
              padding: 32,
            }}
          >
            <h2
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 22,
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: ".02em",
                margin: "0 0 20px",
                color: T.ink,
              }}
            >
              Live Telemetry
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 24,
              }}
            >
              {Object.entries(
                (telemetry.series || []).reduce((acc, r) => {
                  (acc[r.metric] = acc[r.metric] || []).push(r);
                  return acc;
                }, {})
              ).map(([metric, points]) => (
                <div key={metric}>
                  <p
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: ".08em",
                      textTransform: "uppercase",
                      color: T.ink,
                      marginBottom: 8,
                    }}
                  >
                    {metric.replace(/_/g, " ")}
                  </p>
                  <TelemetrySparkline points={points} unit={points[0]?.unit || ""} />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
